import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ImportResult {
  total_inspecoes: number;
  total_riscos: number;
  total_evidencias: number;
  total_encerramentos: number;
  total_hierarquia: number;
  total_taxonomia: number;
  errors: string[];
  duration_ms: number;
  batches_processed?: number;
  corrected_errors?: number;
}

export interface JobProgress {
  status: string;
  processed_records: number;
  total_records: number;
  fase_atual: string | null;
  arquivo_atual: string | null;
  lote_atual: number;
  error_message: string | null;
}

type ImportStatus = 'idle' | 'uploading' | 'processing' | 'completed' | 'error';

const FASE_LABELS: Record<string, string> = {
  aguardando: 'Aguardando início...',
  download_zip: 'Baixando pacote do storage...',
  extraindo_zip: 'Extraindo ZIP...',
  validacao_manifest: 'Validando manifest.json...',
  parsing_jsonl: 'Processando arquivos JSONL...',
  ingestao: 'Inserindo dados no banco...',
  concluido: 'Importação concluída.',
  erro: 'Falha no processamento.',
};

const POLL_INTERVAL_MS = 2_500;
const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes without update = stale

export function useSiafoImport() {
  const [status, setStatus] = useState<ImportStatus>('idle');
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [jobProgress, setJobProgress] = useState<JobProgress | null>(null);
  const pollingRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      window.clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback((jobId: string) => {
    stopPolling();

    pollingRef.current = window.setInterval(async () => {
      const { data: currentJob } = await supabase
        .from('siafo_import_jobs')
        .select('status, resultado, processed_records, total_records, fase_atual, arquivo_atual, lote_atual, error_message, updated_at')
        .eq('id', jobId)
        .maybeSingle();

      if (!currentJob) return;

      const jp: JobProgress = {
        status: currentJob.status,
        processed_records: (currentJob as any).processed_records ?? 0,
        total_records: (currentJob as any).total_records ?? 0,
        fase_atual: (currentJob as any).fase_atual,
        arquivo_atual: (currentJob as any).arquivo_atual,
        lote_atual: (currentJob as any).lote_atual ?? 0,
        error_message: (currentJob as any).error_message,
      };
      setJobProgress(jp);

      // Real progress based on processed/total records
      if (jp.total_records > 0 && jp.processed_records > 0) {
        const pct = Math.min(98, Math.round((jp.processed_records / jp.total_records) * 100));
        setProgress(pct);
      } else if (jp.fase_atual) {
        const phaseProgress: Record<string, number> = {
          download_zip: 5,
          extraindo_zip: 10,
          validacao_manifest: 12,
          parsing_jsonl: 18,
          ingestao: 25,
        };
        setProgress(phaseProgress[jp.fase_atual] ?? 15);
      }

      // Status message
      const faseLabel = FASE_LABELS[jp.fase_atual ?? ''] ?? `Processando (${jp.fase_atual})...`;
      const arquivoSuffix = jp.arquivo_atual ? ` → ${jp.arquivo_atual}` : '';
      setStatusMessage(`${faseLabel}${arquivoSuffix}`);

      // Stale job detection on frontend side
      const updatedAt = (currentJob as any).updated_at;
      if (currentJob.status === 'processing' && updatedAt) {
        const lastUpdate = new Date(updatedAt).getTime();
        if (Date.now() - lastUpdate > STALE_THRESHOLD_MS) {
          setError('Processamento sem resposta há mais de 5 minutos. O job pode ter travado.');
          setStatus('error');
          setStatusMessage('Job sem heartbeat — possível travamento.');
          stopPolling();
          return;
        }
      }

      // Terminal states
      if (currentJob.status === 'completed' || currentJob.status === 'completed_with_errors') {
        if (currentJob.resultado && typeof currentJob.resultado === 'object' && !Array.isArray(currentJob.resultado)) {
          setResult(currentJob.resultado as unknown as ImportResult);
        }
        setStatus('completed');
        setProgress(100);
        setStatusMessage(
          currentJob.status === 'completed_with_errors'
            ? 'Importação concluída com alertas.'
            : 'Importação concluída com sucesso.'
        );
        stopPolling();
      }

      if (currentJob.status === 'failed' || currentJob.status === 'error') {
        setError(jp.error_message ?? 'Falha no processamento.');
        setStatus('error');
        setProgress(0);
        setStatusMessage('Falha no processamento.');
        if (currentJob.resultado && typeof currentJob.resultado === 'object' && !Array.isArray(currentJob.resultado)) {
          setResult(currentJob.resultado as unknown as ImportResult);
        }
        stopPolling();
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling]);

  const runImport = useCallback(async (file: File) => {
    setStatus('uploading');
    setError(null);
    setResult(null);
    setProgress(2);
    setStatusMessage('Criando job de importação...');
    setJobProgress(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      /* 1. Create job (status=uploading) */
      const { data: job, error: jobErr } = await supabase
        .from('siafo_import_jobs')
        .insert({ user_id: user.id, status: 'uploading' })
        .select('id')
        .single();

      if (jobErr) throw new Error(jobErr.message);

      setProgress(5);
      setStatusMessage('Enviando arquivo para armazenamento seguro...');

      /* 2. Upload ZIP to Storage */
      const storagePath = `${job.id}/${file.name}`;
      const { error: uploadErr } = await supabase.storage
        .from('siafo-imports')
        .upload(storagePath, file, { cacheControl: '3600', upsert: false });

      if (uploadErr) throw new Error(`Falha no upload: ${uploadErr.message}`);

      setProgress(15);
      setStatusMessage('Arquivo enviado. Iniciando processamento...');

      /* 3. Update job → pending (decoupled from processing) */
      const { error: updateErr } = await supabase
        .from('siafo_import_jobs')
        .update({
          file_path: storagePath,
          file_name: file.name,
          file_size: file.size,
          status: 'pending',
        } as any)
        .eq('id', job.id);

      if (updateErr) throw new Error(updateErr.message);

      setStatus('processing');
      setProgress(18);

      /* 4. Start polling BEFORE triggering function */
      startPolling(job.id);

      /* 5. Fire-and-forget Edge Function trigger */
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token ?? '';

      fetch(
        `https://${projectId}.supabase.co/functions/v1/siafo-import`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ job_id: job.id }),
        }
      ).catch((fetchErr) => {
        console.warn('siafo-import fetch completed/failed:', fetchErr?.message);
      });

    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setStatusMessage('Falha na importação.');
      setStatus('error');
      setProgress(0);
      stopPolling();
    }
  }, [startPolling, stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    setStatus('idle');
    setResult(null);
    setError(null);
    setProgress(0);
    setStatusMessage('');
    setJobProgress(null);
  }, [stopPolling]);

  return { status, result, error, progress, statusMessage, jobProgress, runImport, reset };
}
